/**
 * LanguageSelector — Top-right language switcher with flag, code, and search.
 * Supports 33 languages across 3 groups (Global, African, Mzansi).
 * Handles RTL layout for Arabic, Hebrew, Persian, Urdu.
 */
import { useState, useEffect, useRef } from "react";
import i18n from "@/services/i18nService";

const GROUP_ORDER = ["Global", "African", "Mzansi"];
const GROUP_COLORS = {
  Global: { bg: "bg-blue-600", text: "text-blue-200", border: "border-blue-500" },
  African: { bg: "bg-amber-600", text: "text-amber-200", border: "border-amber-500" },
  Mzansi: { bg: "bg-green-600", text: "text-green-200", border: "border-green-500" },
};

export default function LanguageSelector({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(i18n.currentLang);
  const [languages, setLanguages] = useState(i18n.getLanguages());
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  // Subscribe to language changes
  useEffect(() => {
    const unsub = i18n.onChange((lang) => {
      setCurrent(lang);
    });
    return unsub;
  }, []);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = languages.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.name.toLowerCase().includes(q) ||
           l.nativeName.toLowerCase().includes(q) ||
           l.code.includes(q);
  });

  const handleSelect = (lang) => {
    i18n.setLanguage(lang.code);
    setOpen(false);
    setSearch("");
  };

  const rtlFlag = current.direction === "rtl";

  return (
    <div className="relative" ref={ref} style={{ direction: rtlFlag ? "rtl" : "ltr" }}>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-lg border transition-all hover:bg-white/10 ${
          compact ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"
        } border-white/20 text-white`}
        title={current.name}
      >
        <span className="text-base leading-none">{current.flagEmoji}</span>
        <span className="font-semibold uppercase tracking-wide text-xs">{current.code}</span>
        <svg className={`w-3 h-3 text-white/60 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className={`absolute top-full mt-1 z-[9999] w-72 max-h-96 overflow-hidden bg-slate-900 border border-white/20 rounded-xl shadow-2xl shadow-black/50 backdrop-blur-xl ${rtlFlag ? "left-0" : "right-0"}`}>
          {/* Search */}
          <div className="p-2 border-b border-white/10">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search language..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-blue-400"
              autoFocus
            />
          </div>

          {/* Language List */}
          <div className="overflow-y-auto max-h-80">
            {GROUP_ORDER.map((group) => {
              const langs = filtered.filter(l => l.group === group);
              if (langs.length === 0) return null;
              const colors = GROUP_COLORS[group];
              return (
                <div key={group}>
                  <div className={`sticky top-0 z-10 px-3 py-1 ${colors.bg} text-xs font-bold uppercase tracking-wider text-white flex items-center justify-between`}>
                    <span>{group}</span>
                    <span className="text-white/60 text-[10px]">{langs.length}</span>
                  </div>
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => handleSelect(l)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                        current.code === l.code
                          ? "bg-blue-600/30 text-white"
                          : "text-slate-300 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <span className="text-lg leading-none">{l.flagEmoji}</span>
                      <span className="flex-1 text-left">{l.nativeName}</span>
                      <span className="text-xs text-white/40 uppercase font-mono">{l.code}</span>
                      {current.code === l.code && (
                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
